import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatUSD } from "@/components/KPICard";
import { formatDMY } from "@/lib/dates";
import { TrendingUp, TrendingDown, Info } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Annual yield card, computed PER CONTRACT YEAR.
 *
 * net_yield_pct = (adjusted_income_usd - real_expenses_usd) / value_usd * 100
 *
 * adjusted_income_usd = paid_amount for collected payments (in USD, at collection FX)
 *                     + expected_amount for still-pending payments (in USD, at today's FX)
 *                     within [year_start, year_end).
 * real_expenses_usd  = expense transactions dated inside the same window.
 *
 * At the start of a year (nothing paid, nothing spent) Net == Gross.
 * As real payments and expenses come in, the % adjusts.
 */
export default function PropertyYieldCard({ propertyId }) {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (yearN) => {
    setLoading(true);
    try {
      const url = yearN
        ? `/dashboard/property_yield/${propertyId}?contract_year=${yearN}`
        : `/dashboard/property_yield/${propertyId}`;
      const r = await api.get(url);
      setData(r.data);
      setYear(r.data.selected_year);
    } catch (_) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [propertyId]);

  if (loading) {
    return (
      <div className="rounded-xl border border-border/60 bg-card p-5 animate-pulse text-sm text-muted-foreground">
        Calculando rendimiento…
      </div>
    );
  }
  if (!data) return null;

  if (!data.has_contract) {
    return (
      <div className="rounded-xl border border-dashed border-border/60 bg-card p-5" data-testid="yield-card-no-contract">
        <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Rendimiento anual</div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Info className="w-4 h-4" />
          {data.message || "Sin contrato para proyectar ingresos."}
        </div>
        {data.value_usd > 0 && (
          <div className="text-xs text-muted-foreground mt-2 num">Valor propiedad: {formatUSD(data.value_usd)}</div>
        )}
      </div>
    );
  }

  const positive = data.net_yield_pct >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const yieldColor = positive ? "text-emerald-500" : "text-rose-500";

  return (
    <div className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 via-card to-card p-5 space-y-4" data-testid="yield-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Rendimiento anual (Neto)</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Año contractual {data.selected_year} de {data.years.length} · {formatDMY(data.year_start)} → {formatDMY(data.year_end)}
          </div>
        </div>
        {data.years.length > 1 && (
          <div className="flex gap-1 flex-wrap">
            {data.years.map((y) => (
              <Button
                key={y.n}
                size="sm"
                variant={y.n === year ? "default" : "outline"}
                className="h-7 px-2 text-xs"
                onClick={() => load(y.n)}
                data-testid={`yield-year-${y.n}`}
              >
                Año {y.n}
              </Button>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-end gap-3">
        <Icon className={`w-8 h-8 ${yieldColor}`} />
        <div className={`text-5xl font-heading font-bold num ${yieldColor}`} data-testid="yield-pct-value">
          {data.net_yield_pct.toFixed(2)}%
        </div>
        <div className="text-xs text-muted-foreground pb-2 num">
          bruto: {data.gross_yield_pct.toFixed(2)}%
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-border/40">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Ingresos ajustados</div>
          <div className="text-sm font-heading font-semibold text-emerald-500 num">{formatUSD(data.adjusted_income_usd)}</div>
          <div className="text-[10px] text-muted-foreground num">
            cobrado: {formatUSD(data.collected_income_usd)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Gastos reales</div>
          <div className="text-sm font-heading font-semibold text-rose-500 num">{formatUSD(data.real_expenses_usd)}</div>
          <div className="text-[10px] text-muted-foreground">solo transacciones registradas</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Neto USD</div>
          <div className={`text-sm font-heading font-semibold num ${positive ? "text-emerald-500" : "text-rose-500"}`}>{formatUSD(data.net_usd)}</div>
          <div className="text-[10px] text-muted-foreground">ingresos − gastos</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Valor propiedad</div>
          <div className="text-sm font-heading font-semibold num">{formatUSD(data.value_usd)}</div>
          <div className="text-[10px] text-muted-foreground">base del %</div>
        </div>
      </div>

      <div className="text-[11px] text-muted-foreground border-t border-border/40 pt-3 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>
          {data.months_collected}/{data.months_collected + data.months_pending} meses cobrados este año contractual.{" "}
          Los meses pendientes se contabilizan al monto estimado; al registrar el cobro real, el % se recalcula solo.
          {data.real_expenses_usd === 0 && data.months_collected === 0 && (
            <> Como todavía no hay gastos ni cobros, el Neto coincide con el Bruto proyectado.</>
          )}
        </div>
      </div>
    </div>
  );
}
